/**
 * Iter64 — Vista /prospeccion/gestoras: ranking de gestoras detectadas en Histórico
 * con filtros, ordenación y CSV.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Trophy, Loader2, RefreshCw, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { req } from '../services/api';

const fmt = (n) => Number(n ?? 0).toLocaleString('es-ES');

export default function ProspeccionGestoras() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCliente, setFiltroCliente] = useState('all');  // all|si|no
  const [filtroVencen, setFiltroVencen] = useState(false);
  const [importeMin, setImporteMin] = useState('');
  const [q, setQ] = useState('');
  const [orderBy, setOrderBy] = useState('municipios_totales');

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroCliente === 'si') params.set('es_cliente', 'true');
      if (filtroCliente === 'no') params.set('es_cliente', 'false');
      if (filtroVencen) params.set('contratos_vencen_12m', 'true');
      if (importeMin) params.set('importe_min', importeMin);
      const url = `/api/prospeccion/gestoras${params.toString() ? '?' + params.toString() : ''}`;
      const d = await req(url);
      setData(d || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtroCliente, filtroVencen, importeMin]);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    let arr = data;
    if (qLower) arr = arr.filter((r) => (r.razon_social || '').toLowerCase().includes(qLower));
    return [...arr].sort((a, b) => (b[orderBy] || 0) - (a[orderBy] || 0));
  }, [data, q, orderBy]);

  const exportarCSV = () => {
    const rows = [['Razón social', 'NIF', 'Grupo', 'Municipios', 'Población', 'Activos', 'Vencen<12m', 'Importe', 'Cliente']];
    filtered.forEach((g) => rows.push([
      g.razon_social || '', g.nif || '', g.grupo_nombre || '',
      g.municipios_totales, g.poblacion_total, g.contratos_activos,
      g.contratos_vencen_12m, Math.round(g.importe_total_gestionado),
      g.es_cliente ? 'Sí' : 'No',
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'gestoras_iter64.csv';
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="page-gestoras">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink-900 flex items-center gap-2">
            <Trophy className="w-7 h-7 text-brand-500" /> Ranking de gestoras
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Empresas que han ganado licitaciones de agua en España, agrupadas por
            municipios, contratos activos y población gestionada.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-gestoras-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={exportarCSV} disabled={!filtered.length} data-testid="btn-gestoras-csv">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      <Card className="p-4 border-ink-200 bg-white">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <Input
              placeholder="Buscar por razón social…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9" data-testid="gestoras-search"
            />
          </div>
          <select
            className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
            value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}
            data-testid="gestoras-filtro-cliente"
          >
            <option value="all">Todos</option>
            <option value="si">Solo clientes</option>
            <option value="no">Solo no-clientes</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox" className="accent-brand-500"
              checked={filtroVencen} onChange={(e) => setFiltroVencen(e.target.checked)}
              data-testid="gestoras-filtro-vencen"
            />
            Vencen contratos &lt; 12 meses
          </label>
          <Input
            type="number" placeholder="Importe min (€)"
            value={importeMin} onChange={(e) => setImporteMin(e.target.value)}
            className="w-40" data-testid="gestoras-filtro-importe"
          />
          <select
            className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
            value={orderBy} onChange={(e) => setOrderBy(e.target.value)}
            data-testid="gestoras-orderby"
          >
            <option value="municipios_totales">Por municipios</option>
            <option value="poblacion_total">Por población</option>
            <option value="contratos_vencen_12m">Por contratos &lt;12m</option>
            <option value="importe_total_gestionado">Por importe total</option>
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="py-12 text-center text-ink-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-ink-200 text-ink-400">
          Sin gestoras detectadas todavía. Importa años en <span className="font-semibold">/prospeccion/historico</span>.
        </Card>
      ) : (
        <Card className="border-ink-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="gestoras-tabla">
            <thead>
              <tr className="text-left text-xs text-ink-500 border-b border-ink-100">
                <th className="px-4 py-2.5">Razón social</th>
                <th className="px-3 py-2.5">Grupo</th>
                <th className="px-3 py-2.5 text-right">Municipios</th>
                <th className="px-3 py-2.5 text-right">Población</th>
                <th className="px-3 py-2.5 text-right">Activos</th>
                <th className="px-3 py-2.5 text-right">Vencen &lt;12m</th>
                <th className="px-3 py-2.5 text-right">Importe €</th>
                <th className="px-3 py-2.5">Cliente</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr
                  key={g.entidad_id}
                  onClick={() => navigate(`/clientes/${g.entidad_id}`)}
                  className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                  data-testid={`gestora-fila-${g.entidad_id}`}
                >
                  <td className="px-4 py-2.5 font-medium text-ink-900">{g.razon_social}</td>
                  <td className="px-3 py-2.5 text-ink-500">{g.grupo_nombre || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(g.municipios_totales)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(g.poblacion_total)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(g.contratos_activos)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {g.contratos_vencen_12m > 0
                      ? <span className="text-amber-700 font-semibold">{g.contratos_vencen_12m}</span>
                      : <span className="text-ink-300">0</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(Math.round(g.importe_total_gestionado))}</td>
                  <td className="px-3 py-2.5">
                    {g.es_cliente
                      ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">CLIENTE</Badge>
                      : <Badge className="bg-ink-50 text-ink-600 border border-ink-200">LEAD</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
